import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, PartyPopper } from "lucide-react";
import api from "@/lib/api";
import clientApi from "@/lib/clientApi";
import { useClientAuth } from "@/context/ClientAuthContext";
import Seo from "@/components/Seo";

const LS_KEY = "hm_moving_checklist";
const loadLocal = () => {
  try { return new Set(JSON.parse(localStorage.getItem(LS_KEY) || "[]")); }
  catch { return new Set(); }
};

export default function MovingChecklist() {
  const { client } = useClientAuth();
  const [data, setData] = useState(null);
  const [checked, setChecked] = useState(loadLocal);
  const syncedRef = useRef(false);
  const saveTimer = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/moving-checklist").then((r) => setData(r.data)).catch(() => setData({ categories: [], items: [] }));
  }, []);

  // Logged-in: merge server progress with local once, then server becomes source of truth
  useEffect(() => {
    if (!client || !data || syncedRef.current) return;
    syncedRef.current = true;
    const validIds = new Set(data.items.map((i) => i.id));
    clientApi.get("/client/checklist-progress").then((r) => {
      setChecked((local) => {
        const merged = new Set([...local, ...(r.data.checked || [])].filter((id) => validIds.has(id)));
        localStorage.setItem(LS_KEY, JSON.stringify([...merged]));
        clientApi.put("/client/checklist-progress", { checked: [...merged] }).catch(() => {});
        return merged;
      });
    }).catch(() => {});
  }, [client, data]);

  const toggle = (id) => {
    setChecked((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      localStorage.setItem(LS_KEY, JSON.stringify([...next]));
      if (client) {
        clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => {
          clientApi.put("/client/checklist-progress", { checked: [...next] }).catch(() => {});
        }, 600);
      }
      return next;
    });
  };

  const byCategory = useMemo(() => {
    const map = {};
    (data?.items || []).forEach((i) => { (map[i.category] = map[i.category] || []).push(i); });
    return map;
  }, [data]);

  if (!data) return <div className="py-32 text-center text-charcoal-500">Loading…</div>;
  const total = data.items.length;
  const done = data.items.filter((i) => checked.has(i.id)).length;
  const pct = total ? Math.round((done / total) * 100) : 0;

  return (
    <div data-testid="page-moving-checklist" className="mx-auto max-w-4xl px-6 py-14 lg:py-20">
      <Seo title="Moving to Mexico Checklist | Hola Movers" description="An interactive checklist covering visas, housing, healthcare and pets — track your progress moving to Mérida, Mexico." path="/moving-checklist" />
      <h1 className="text-4xl sm:text-5xl font-light tracking-tight text-charcoal-900">Your moving checklist</h1>
      <p className="mt-4 max-w-2xl text-lg text-charcoal-600">
        Everything that matters for a calm move to Mérida — visas, housing, healthcare and pets. Check things off as you go; your progress is saved.
      </p>

      <div className="mt-8 rounded-2xl border border-sand-200 bg-white p-5" data-testid="checklist-overall">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium text-charcoal-800">Overall progress</span>
          <span data-testid="checklist-overall-pct" className="font-serif text-lg text-terracotta">{done}/{total} · {pct}%</span>
        </div>
        <div className="mt-2 h-2.5 overflow-hidden rounded-full bg-sand-200">
          <div className="h-full rounded-full bg-terracotta transition-all duration-500" style={{ width: `${pct}%` }} />
        </div>
        {pct === 100 && (
          <p data-testid="checklist-complete-msg" className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-sage">
            <PartyPopper size={16} /> You're Mérida-ready! We can't wait to welcome you.
          </p>
        )}
      </div>

      {!client && (
        <div data-testid="checklist-guest-banner" className="mt-4 rounded-xl bg-sage-light px-4 py-3 text-sm text-charcoal-700">
          Progress is saved in this browser.{" "}
          <Link to="/account" className="font-medium text-terracotta hover:underline">Create a free account</Link>{" "}
          to keep it across devices — and unlock your client portal.
        </div>
      )}

      <div className="mt-10 space-y-8">
        {data.categories.map((cat) => {
          const items = byCategory[cat.id] || [];
          const catDone = items.filter((i) => checked.has(i.id)).length;
          return (
            <section key={cat.id} data-testid={`checklist-category-${cat.id}`} className="rounded-2xl border border-sand-200 bg-white overflow-hidden">
              <div className="flex items-center justify-between border-b border-sand-100 bg-sand-100/50 px-5 py-4">
                <h2 className="flex items-center gap-2 font-serif text-lg text-charcoal-800">
                  <span aria-hidden="true">{cat.emoji}</span> {cat.label}
                </h2>
                <span className="text-sm text-charcoal-500">{catDone}/{items.length}</span>
              </div>
              <ul className="divide-y divide-sand-100">
                {items.map((item) => {
                  const on = checked.has(item.id);
                  return (
                    <li key={item.id}>
                      <button type="button" data-testid={`checklist-item-${item.id}`} onClick={() => toggle(item.id)}
                        className="flex w-full items-start gap-3 px-5 py-3.5 text-left transition-colors hover:bg-sand-50">
                        {on
                          ? <CheckCircle2 size={20} className="mt-0.5 shrink-0 text-sage" />
                          : <Circle size={20} className="mt-0.5 shrink-0 text-sand-300" />}
                        <span>
                          <span className={`block text-sm font-medium ${on ? "text-charcoal-400 line-through" : "text-charcoal-800"}`}>{item.label}</span>
                          {item.note && <span className={`block text-xs mt-0.5 ${on ? "text-charcoal-300" : "text-charcoal-500"}`}>{item.note}</span>}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}
      </div>

      <div className="mt-12 rounded-2xl bg-sage-light p-6 text-center">
        <p className="font-medium text-charcoal-800">Feeling overwhelmed by any of these?</p>
        <p className="mt-1 text-sm text-charcoal-600">That's literally what we're here for — visas, housing, healthcare, pets, all of it.</p>
        <Link to="/book" data-testid="checklist-book-cta" className="mt-4 inline-block rounded-full bg-terracotta px-6 py-2.5 text-sm font-medium text-white hover:bg-terracotta-hover">Book an intro call</Link>
      </div>
    </div>
  );
}
